import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * NavigationContext
 * 
 * Provides a way for pages to:
 * 1. Register custom back navigation handlers (for wizards)
 * 2. Register unsaved changes state (for forms)
 * 
 * The global BackButton will respect these registrations and show
 * appropriate confirmation dialogs when needed.
 * 
 * Usage for wizards:
 * ```
 * const { registerBackHandler, unregisterBackHandler } = useNavigation();
 * 
 * useEffect(() => {
 *   registerBackHandler({
 *     label: currentStep > 1 ? `Step ${currentStep - 1}` : 'Exit',
 *     onBack: () => {
 *       if (currentStep > 1) {
 *         setCurrentStep(prev => prev - 1);
 *       } else {
 *         showExitConfirmation();
 *       }
 *     }
 *   });
 *   return () => unregisterBackHandler();
 * }, [currentStep]);
 * ```
 * 
 * Usage for forms with unsaved changes:
 * ```
 * const { registerUnsavedChanges, unregisterUnsavedChanges } = useNavigation();
 * 
 * useEffect(() => {
 *   registerUnsavedChanges({
 *     hasChanges: hasUnsavedChanges,
 *     onConfirmLeave: () => navigate('/'),
 *     message: 'You have unsaved changes in this form.'
 *   });
 *   return () => unregisterUnsavedChanges();
 * }, [hasUnsavedChanges]);
 * ```
 */

const NavigationContext = createContext(null);

export function NavigationProvider({ children }) {
  // Back button handler state (for wizards)
  const [backHandler, setBackHandler] = useState(null);
  
  // Unsaved changes state (for forms)
  const [unsavedChangesState, setUnsavedChangesState] = useState(null);
  
  // Pending navigation (when blocked by unsaved changes)
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Back handler registration (for wizards)
  const registerBackHandler = useCallback((handler) => {
    setBackHandler(handler);
  }, []);

  const unregisterBackHandler = useCallback(() => {
    setBackHandler(null);
  }, []);
  
  // Unsaved changes registration (for forms)
  const registerUnsavedChanges = useCallback((state) => {
    setUnsavedChangesState(state);
  }, []);
  
  const unregisterUnsavedChanges = useCallback(() => {
    setUnsavedChangesState(null);
  }, []);
  
  // Check if navigation should be blocked
  const shouldBlockNavigation = useCallback(() => {
    return unsavedChangesState?.hasChanges === true;
  }, [unsavedChangesState]);
  
  // Request navigation (will be blocked if unsaved changes)
  const requestNavigation = useCallback((callback) => {
    if (shouldBlockNavigation()) {
      setPendingNavigation(() => callback);
      setShowUnsavedDialog(true);
      return false;
    }
    callback();
    return true;
  }, [shouldBlockNavigation]);
  
  // Confirm leaving (discard changes)
  const confirmLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);
  
  // Cancel leaving (stay on page)
  const cancelLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  }, []);

  const value = {
    // Back handler (wizards)
    backHandler,
    registerBackHandler,
    unregisterBackHandler,
    hasCustomBackHandler: !!backHandler,
    
    // Unsaved changes (forms)
    unsavedChangesState,
    registerUnsavedChanges,
    unregisterUnsavedChanges,
    hasUnsavedChanges: unsavedChangesState?.hasChanges === true,
    
    // Navigation control
    shouldBlockNavigation,
    requestNavigation,
    showUnsavedDialog,
    confirmLeave,
    cancelLeave,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

export default NavigationContext;